/**
 * AppSidebar — Vertical sidebar navigation for the authenticated app.
 *
 * Renders main navigation items with expandable nested subtabs.
 * Active state is determined from the current URL path + ?tab= search param.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import {
  Users,
  Brain,
  Sparkles,
  BarChart3,
  CalendarDays,
  ClipboardList,
  ClipboardCheck,
  Settings,
  LogOut,
  ChevronRight,
  HelpCircle,
  Moon,
  Sun,
  Share2,
  PanelLeftClose,
  PanelLeft,
  Target,
  MessageSquare,
  Activity,
  Mail,
  GitBranch,
  Clock,
  TrendingUp,
  User,
  BookOpen,
  Rocket,
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { readAccess } from '../../constants/featureAccess';

/**
 * Navigation definition — exported so AppHeader can build breadcrumbs.
 */
export const NAVIGATION = [
  {
    name: 'Dashboard',
    href: '/app/dashboard',
    icon: BarChart3,
    anyOf: readAccess('accounts'),
    defaultTab: null,
    subtabs: [],
  },
  {
    name: 'Accounts',
    href: '/app/accounts',
    icon: Users,
    anyOf: readAccess('accounts'),
    defaultTab: null,
    subtabs: [],
  },
  {
    name: 'Onboarding',
    href: '/app/onboarding',
    icon: Rocket,
    anyOf: readAccess('onboarding'),
    defaultTab: null,
    subtabs: [
      { name: 'Playbook', href: '/app/onboarding', icon: Rocket },
      { name: 'Insights', href: '/app/onboarding/insights', icon: BarChart3 },
    ],
  },
  {
    name: 'AI Actions',
    href: '/app/ai-actions',
    icon: Sparkles,
    anyOf: [...readAccess('accounts'), ...readAccess('campaigns'), ...readAccess('workflows')],
    defaultTab: 'priority',
    subtabs: [
      { name: 'Priority Actions', tabValue: 'priority', icon: Target },
      { name: 'Pending Actions', tabValue: 'pending', icon: Clock },
      { name: 'Campaigns', tabValue: 'campaigns', icon: Mail },
      { name: 'Workflows', tabValue: 'workflows', icon: GitBranch },
    ],
  },
  {
    name: 'Communications',
    href: '/app/communications',
    icon: Brain,
    anyOf: readAccess('communications'),
    defaultTab: 'conversations',
    subtabs: [
      { name: 'Conversations', tabValue: 'conversations', icon: MessageSquare },
      { name: 'AI Activity', tabValue: 'activity', icon: Activity },
      { name: 'Insights', tabValue: 'insights', icon: BarChart3 },
    ],
  },
  {
    name: 'Analytics',
    href: '/app/analytics',
    icon: BarChart3,
    anyOf: readAccess('analytics'),
    defaultTab: 'executive',
    subtabs: [
      { name: 'Account Health', tabValue: 'executive', icon: BarChart3 },
      { name: 'Revenue', tabValue: 'revenue_trends', icon: TrendingUp },
      { name: 'Cohorts', tabValue: 'cohort_analysis', icon: Users },
      { name: 'Scenarios', tabValue: 'scenario_planning', icon: Target },
      { name: 'Team Performance', tabValue: 'team_performance', icon: Users },
    ],
  },
  {
    name: 'Meetings',
    href: '/app/meetings',
    icon: CalendarDays,
    anyOf: readAccess('meetings'),
    defaultTab: null,
    subtabs: [],
  },
  {
    name: 'Tasks',
    href: '/app/tasks-board',
    icon: ClipboardList,
    anyOf: readAccess('tasks'),
    defaultTab: null,
    subtabs: [],
  },
  {
    // Surveys lives inside the AIActions page (rendered when ?tab=surveys),
    // but is surfaced as its own main-nav entry so users don't have to drill
    // into AI Actions to find it. `basePath` + `matchTab` tell the active-state
    // matcher to claim this entry only when the URL's ?tab= equals 'surveys'.
    name: 'Surveys',
    href: '/app/ai-actions?tab=surveys',
    basePath: '/app/ai-actions',
    matchTab: 'surveys',
    icon: ClipboardCheck,
    anyOf: [...readAccess('campaigns'), ...readAccess('workflows')],
    defaultTab: null,
    subtabs: [],
  },
  {
    name: 'Opportunities',
    href: '/app/opportunities',
    icon: TrendingUp,
    anyOf: readAccess('accounts'),
    defaultTab: null,
    subtabs: [],
  },
  {
    name: 'Caleb Chat',
    href: '/app/chat',
    icon: MessageSquare,
    anyOf: readAccess('assistant'),
    defaultTab: null,
    subtabs: [],
  },
  {
    name: 'Knowledge Base',
    href: '/app/knowledge-base',
    icon: BookOpen,
    anyOf: readAccess('knowledge_base'),
    defaultTab: null,
    subtabs: [],
  },
];

const AppSidebar = ({ user, onLogout, collapsed, onToggleCollapse }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { toggleTheme, isDark } = useTheme();
  const { hasAnyPermission } = useAuth();
  const [expandedItem, setExpandedItem] = useState(null);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const hoverTimeoutRef = React.useRef(null);

  // Hide nav entries the user cannot open (mirrors the route-level guard).
  const visibleNavigation = useMemo(
    () => NAVIGATION.filter((item) => !item.anyOf || hasAnyPermission(item.anyOf)),
    [hasAnyPermission]
  );

  // Determine active navigation item from current path.
  // Tab-bound items (e.g. Surveys at /app/ai-actions?tab=surveys) win over
  // their pathname-sharing siblings so the correct entry highlights.
  const currentTab = searchParams.get('tab');
  const activeNavItem =
    NAVIGATION.find(
      (item) =>
        item.matchTab &&
        location.pathname === item.basePath &&
        currentTab === item.matchTab
    ) ||
    NAVIGATION.find(
      (item) =>
        !item.matchTab &&
        (location.pathname === item.href ||
          location.pathname.startsWith(item.href + '/'))
    );

  // Auto-expand the active section
  useEffect(() => {
    if (activeNavItem) {
      setExpandedItem(activeNavItem.href);
    } else {
      // On pages not in NAVIGATION (e.g. Settings), collapse all menus
      setExpandedItem(null);
    }
  }, [activeNavItem?.href]);

  // Hover handlers — expand subtabs on hover only in collapsed mode (flyout tooltips)
  const handleMouseEnter = (item) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    if (item.subtabs.length > 0) {
      setHoveredItem(item.href);
    }
  };

  const handleMouseLeave = (item) => {
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredItem(null);
    }, 200);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    };
  }, []);

  const handleNavClick = (item) => {
    if (item.subtabs.length > 0) {
      if (expandedItem === item.href && location.pathname === item.href) {
        // Already on this page and expanded — just toggle collapse
        setExpandedItem(null);
      } else {
        setExpandedItem(item.href);
        if (location.pathname !== item.href) {
          navigate(item.href);
        }
      }
    } else {
      navigate(item.href);
    }
  };

  const handleSubtabClick = (item, subtab) => {
    if (subtab.href) {
      navigate(subtab.href);
      return;
    }
    const params = new URLSearchParams();
    if (subtab.tabValue !== item.defaultTab) {
      params.set('tab', subtab.tabValue);
    }
    const search = params.toString();
    navigate(`${item.href}${search ? '?' + search : ''}`);
  };

  const isSubtabActive = (item, subtab) => {
    if (subtab.href) {
      return location.pathname === subtab.href;
    }
    if (location.pathname !== item.href) return false;
    const tab = currentTab || item.defaultTab;
    return tab === subtab.tabValue;
  };

  // Close user menu on outside click
  useEffect(() => {
    if (!showUserMenu) return;
    const close = () => setShowUserMenu(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [showUserMenu]);

  /* ───────── Collapsed state ───────── */
  if (collapsed) {
    return (
      <aside className="w-14 bg-card border-r border-border flex flex-col h-full flex-shrink-0 z-40 overflow-visible">
        <div className="h-12 flex items-center justify-center border-b border-border flex-shrink-0">
          <img
            src="/favicon.ico"
            alt="atEaseAI"
            className="h-7 w-7 object-contain"
          />
        </div>

        <nav className="flex-1 py-3 px-2 space-y-1">
          {visibleNavigation.map((item) => {
            const Icon = item.icon;
            const isActive = activeNavItem?.href === item.href;
            const hasSubtabs = item.subtabs.length > 0;
            const isHovered = hoveredItem === item.href;
            return (
              <div
                key={item.href}
                className="relative group"
                onMouseEnter={() => handleMouseEnter(item)}
                onMouseLeave={() => handleMouseLeave(item)}
              >
                <button
                  onClick={() => handleNavClick(item)}
                  className={`w-full p-2.5 rounded-lg flex items-center justify-center transition-colors ${
                    isActive
                      ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                      : 'text-muted-foreground hover:bg-accent'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                </button>

                {/* Tooltip / flyout on hover — collapsed mode */}
                {isHovered && (
                  <>
                    {/* Invisible bridge so mouse can travel from icon to popover */}
                    <div className="absolute left-full top-0 w-3 h-full z-50" />

                    <div
                      className={`absolute left-full top-0 ml-3 bg-popover rounded-lg shadow-xl border border-border z-50 ${
                        hasSubtabs ? 'w-52 py-1' : 'py-0'
                      }`}
                      style={{ filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.1))' }}
                    >
                      {/* Arrow/caret pointing left */}
                      <div className="absolute -left-1.5 top-3 w-3 h-3 bg-popover border-l border-b border-border rotate-45" />

                      {hasSubtabs ? (
                        <>
                          <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider border-b border-border">
                            {item.name}
                          </div>
                          {item.subtabs.map((subtab) => {
                            const SubIcon = subtab.icon;
                            const isSubActive = isSubtabActive(item, subtab);
                            return (
                              <button
                                key={subtab.href || subtab.tabValue}
                                onClick={() => handleSubtabClick(item, subtab)}
                                className={`w-full flex items-center px-3 py-2 text-sm transition-colors ${
                                  isSubActive
                                    ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 font-medium'
                                    : 'text-foreground hover:bg-accent'
                                }`}
                              >
                                <SubIcon className={`h-4 w-4 mr-2.5 flex-shrink-0 ${isSubActive ? 'text-blue-600 dark:text-blue-400' : 'text-muted-foreground dark:text-muted-foreground'}`} />
                                <span>{subtab.name}</span>
                              </button>
                            );
                          })}
                        </>
                      ) : (
                        <div className="px-3 py-2 text-sm font-medium text-foreground whitespace-nowrap">
                          {item.name}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}

          <Separator className="my-2" />

          <button
            onClick={() => navigate('/app/settings')}
            title="Settings"
            className={`w-full p-2.5 rounded-lg flex items-center justify-center transition-colors ${
              location.pathname === '/app/settings'
                ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                : 'text-muted-foreground hover:bg-accent'
            }`}
          >
            <Settings className="h-5 w-5" />
          </button>
        </nav>

        <div className="border-t border-border p-2 flex-shrink-0">
          <button
            onClick={onToggleCollapse}
            className="w-full p-2 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-accent transition-colors"
            title="Expand sidebar"
          >
            <PanelLeft className="h-5 w-5" />
          </button>
        </div>
      </aside>
    );
  }

  /* ───────── Expanded state ───────── */
  return (
    <aside className="w-64 bg-card border-r border-border flex flex-col h-full flex-shrink-0">
      {/* Logo + collapse toggle */}
      <div className="h-12 flex items-center px-4 border-b border-border flex-shrink-0">
        <img
          src="/atease-logo.png"
          alt="atEaseAI"
          className="h-8 w-auto"
        />
        <button
          onClick={onToggleCollapse}
          className="ml-auto p-1.5 rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          title="Collapse sidebar"
        >
          <PanelLeftClose className="h-4 w-4" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-3">
        <div className="space-y-1">
          {visibleNavigation.map((item) => {
            const Icon = item.icon;
            const isActive = activeNavItem?.href === item.href;
            const isExpanded = expandedItem === item.href;
            const hasSubtabs = item.subtabs.length > 0;

            return (
              <div
                key={item.href}
                onMouseEnter={() => handleMouseEnter(item)}
                onMouseLeave={() => handleMouseLeave(item)}
              >
                {/* Parent nav item */}
                <button
                  onClick={() => handleNavClick(item)}
                  className={`w-full flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-all group ${
                    isActive || isExpanded
                      ? 'bg-blue-600 dark:bg-blue-600 text-white'
                      : 'text-foreground hover:bg-accent'
                  }`}
                >
                  <Icon
                    className={`h-5 w-5 mr-3 flex-shrink-0 ${
                      isActive || isExpanded
                        ? 'text-white'
                        : 'text-muted-foreground dark:text-muted-foreground group-hover:text-muted-foreground dark:group-hover:text-gray-300'
                    }`}
                  />
                  <span className="flex-1 text-left">{item.name}</span>
                  {hasSubtabs && (
                    <ChevronRight
                      className={`h-4 w-4 transition-transform duration-200 ${
                        isActive || isExpanded ? 'text-white' : 'text-muted-foreground'
                      } ${isExpanded ? 'rotate-90' : ''}`}
                    />
                  )}
                </button>

                {/* Subtabs (animated expand/collapse) */}
                {hasSubtabs && (
                  <div
                    className="overflow-hidden transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]"
                    style={{
                      maxHeight: isExpanded ? `${item.subtabs.length * 40 + 8}px` : '0px',
                      opacity: isExpanded ? 1 : 0,
                    }}
                  >
                    <div className="mt-1 ml-4 pl-4 border-l-2 border-border space-y-0.5">
                      {item.subtabs.map((subtab) => {
                        const SubIcon = subtab.icon;
                        const isSubActive = isSubtabActive(item, subtab);
                        return (
                          <button
                            key={subtab.href || subtab.tabValue}
                            onClick={() => handleSubtabClick(item, subtab)}
                            className={`w-full flex items-center px-3 py-2 rounded-md text-sm transition-colors ${
                              isSubActive
                                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 font-medium'
                                : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                            }`}
                          >
                            <SubIcon
                              className={`h-4 w-4 mr-2.5 flex-shrink-0 ${
                                isSubActive
                                  ? 'text-blue-600 dark:text-blue-400'
                                  : 'text-muted-foreground dark:text-muted-foreground'
                              }`}
                            />
                            <span>{subtab.name}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <Separator className="my-4" />

        {/* Settings link */}
        <button
          onClick={() => navigate('/app/settings')}
          className={`w-full flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
            location.pathname === '/app/settings'
              ? 'bg-blue-600 dark:bg-blue-600 text-white'
              : 'text-foreground hover:bg-accent'
          }`}
        >
          <Settings className={`h-5 w-5 mr-3 flex-shrink-0 ${
            location.pathname === '/app/settings' ? 'text-white' : 'text-muted-foreground dark:text-muted-foreground'
          }`} />
          <span>Settings</span>
        </button>
      </nav>

      {/* Bottom section */}
      <div className="border-t border-border p-3 flex-shrink-0">
        {/* Quick action icons */}
        <div className="flex items-center gap-1 mb-3">
          <button
            onClick={toggleTheme}
            className="flex-1 flex items-center justify-center p-1.5 rounded-lg text-muted-foreground hover:bg-accent transition-colors"
            title={isDark ? 'Light mode' : 'Dark mode'}
          >
            {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <button
            onClick={() => window.open('https://help.ateaseai.com', '_blank')}
            className="flex-1 flex items-center justify-center p-1.5 rounded-lg text-muted-foreground hover:bg-accent transition-colors"
            title="Help & Support"
          >
            <HelpCircle className="h-4 w-4" />
          </button>
          <button
            onClick={() => navigate('/app/refer')}
            className="flex-1 flex items-center justify-center p-1.5 rounded-lg text-muted-foreground hover:bg-accent transition-colors"
            title="Refer a Business"
          >
            <Share2 className="h-4 w-4" />
          </button>
        </div>

        {/* User avatar + menu */}
        <div className="relative" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="w-full flex items-center p-2 rounded-lg hover:bg-accent transition-colors"
          >
            <Avatar className="h-8 w-8 mr-3 flex-shrink-0">
              <AvatarFallback className="bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-sm">
                {user?.full_name ? user.full_name.charAt(0).toUpperCase() : 'U'}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 text-left min-w-0">
              <div className="text-sm font-medium text-foreground truncate">
                {user?.full_name}
              </div>
              <div className="text-xs text-muted-foreground truncate">
                {user?.email}
              </div>
            </div>
          </button>

          {showUserMenu && (
            <div className="absolute bottom-full left-0 right-0 mb-1 bg-popover rounded-lg shadow-lg border border-border py-1 z-50">
              <button
                onClick={() => {
                  navigate('/app/settings');
                  setShowUserMenu(false);
                }}
                className="w-full flex items-center px-3 py-2 text-sm text-foreground hover:bg-accent"
              >
                <User className="h-4 w-4 mr-2" />
                My Account
              </button>
              <Separator className="my-1" />
              <button
                onClick={() => {
                  onLogout();
                  setShowUserMenu(false);
                }}
                className="w-full flex items-center px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </button>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
};

export default AppSidebar;
